import {
  ChakraBox,
  RowObject,
  TableColumnV2,
  TableV2,
  useDimensions,
} from '@equip.health/ui';

import { useCallback, useMemo, useRef, useState } from 'react';
import DocumentsTableRow from './DocumentsTableRow';
import EditDocumentModal from '../../common/EditDocumentModal';
import fileUtil from '~/lib/util/file.util';
import DocumentPreviewModal from '../../common/DocumentPreviewModal';
import { useLazyGetPatientDocumentDownloadUrlQuery } from '~/store/services/documents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { DocumentCenterEvents } from '~/analytics/documentCenterEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { FileSourceType } from '~/lib/constants/documentCenter.constants';
import { useHistory } from 'react-router-dom';

interface DocumentsTableV2Props {
  documents?: PatientDocumentV2[];
  isLoading?: boolean;
  id: string;
  onVoidClick: (doc: PatientDocumentV2) => void;
  onLoadMoreDocuments?: () => void;
}
const columns: TableColumnV2<PatientDocumentsTableRow>[] = [
  { header: 'Name', key: 'name', width: '350px' },
  { header: 'Document type', key: 'documentType' },
  { header: 'Uploaded', key: 'uploaded' },
  { header: 'Source', key: 'source' },
];

const TABLE_BOTTOM_PADDING = 40;
const INFECTED_FILE_STATUSES = 'INFECTED';
const getFormattedDocFileName = (
  documentExternalId: string,
  fileName: string,
  fileSource: string,
) => {
  if (fileSource === FileSourceType.DOC) {
    return fileName.startsWith(documentExternalId)
      ? fileName
      : `${documentExternalId}-${fileName}`;
  }
  return fileName;
};

const DocumentsTableV2 = ({
  documents,
  id,
  isLoading,
  onVoidClick,
  onLoadMoreDocuments,
}: DocumentsTableV2Props) => {
  const { trackEvent } = useAnalytics();
  const history = useHistory();
  const [isEditDocumentModalOpen, setIsEditDocumentModalOpen] =
    useState<boolean>(false);
  const [documentToEdit, setDocumentToEdit] =
    useState<PatientDocumentV2 | null>(null);
  const [previewModalData, setPreviewModalData] = useState<{
    name: string;
    url: string;
  } | null>(null);
  const [invalidDocuments, setInvalidDocuments] = useState<
    Record<string, boolean>
  >({});
  const [getDownloadUrl] = useLazyGetPatientDocumentDownloadUrlQuery();

  const openEditDocumentModal = useCallback((document: PatientDocumentV2) => {
    setIsEditDocumentModalOpen(true);
    setDocumentToEdit(document);
  }, []);
  const handleDownload = useCallback(
    async (doc: PatientDocumentV2) => {
      const formattedFileName = getFormattedDocFileName(
        doc.documentExternalId,
        doc.fileName,
        doc.fileSource,
      );
      const result = await getDownloadUrl({
        patientId: doc.patientExternalId,
        fileName: formattedFileName,
        documentExternalId: doc.documentExternalId,
      }).unwrap();
      if (!result.isValidFile) {
        setInvalidDocuments((prev) => ({
          ...prev,
          [doc.documentExternalId]: true,
        }));
        return;
      }
      if (result.attachmentDownloadUrl) {
        fileUtil.triggerFileDownload(
          result.attachmentDownloadUrl,
          doc.fileName,
        );
      }
    },
    [getDownloadUrl],
  );

  const rows: RowObject<PatientDocumentsTableRow>[] = useMemo(
    () =>
      documents?.map((document) =>
        DocumentsTableRow({
          document,
          id,
          onVoidClick,
          openEditDocumentModal,
          isInvalidFile: !!invalidDocuments[document.documentExternalId],
          onDownloadClick: () => handleDownload(document),
          trackEvent,
          history,
        }),
      ),
    [documents, id, openEditDocumentModal, onVoidClick, invalidDocuments],
  );
  const tableRef = useRef<HTMLDivElement>(null);

  const tableYPosition = useDimensions(tableRef, true)?.borderBox.top;
  const windowHeight = window?.innerHeight;
  const tableHeight = windowHeight - tableYPosition - TABLE_BOTTOM_PADDING;

  const handleRowClick = async ({ key }: { key: string }) => {
    const document = documents?.find((doc) => doc.documentExternalId === key);

    if (!document || document?.fileScanStatus === INFECTED_FILE_STATUSES)
      return;

    trackEvent(DocumentCenterEvents.fileEntryClicked, {
      Source: AnalyticsSourceValues.DOCUMENT_CENTER,
      DocumentExternalId: document.documentExternalId,
    });

    try {
      const formattedFileName = getFormattedDocFileName(
        document.documentExternalId,
        document.fileName,
        document.fileSource,
      );
      const result = await getDownloadUrl({
        patientId: document.patientExternalId,
        fileName: formattedFileName,
        documentExternalId: document.documentExternalId,
      }).unwrap();

      if (result.isValidFile === false) {
        setInvalidDocuments((prev) => ({
          ...prev,
          [document.documentExternalId]: true,
        }));
        return;
      }

      if (result.attachmentDownloadUrl && result.isValidFile) {
        if (fileUtil.canShowPreview(document.fileName)) {
          setPreviewModalData({
            name: document.fileName,
            url: result.attachmentDownloadUrl,
          });
        } else {
          window.open(result.attachmentDownloadUrl, '_blank', 'noopener');
        }
      }
    } catch (error) {
      console.error('Failed to get download URL', error);
    }
  };

  return (
    <>
      <ChakraBox
        height={tableHeight}
        id={`${id}__table-container`}
        ref={tableRef}
        sx={{
          'th:first-of-type': {
            paddingLeft: '55px !important',
          },
          'td, th': {
            boxShadow: 'none',
          },
        }}
      >
        <TableV2
          columns={columns}
          id={`${id}__table`}
          isInfiniteScrollEnabled
          isLoading={isLoading}
          onInfiniteScroll={onLoadMoreDocuments}
          onRowClick={handleRowClick}
          rows={rows}
        />
      </ChakraBox>
      {isEditDocumentModalOpen && documentToEdit && (
        <EditDocumentModal
          documentDetails={documentToEdit}
          id={`${id}__edit-document-modal`}
          isOpen={isEditDocumentModalOpen}
          onClose={() => setIsEditDocumentModalOpen(false)}
        />
      )}
      {previewModalData && (
        <DocumentPreviewModal
          fileName={previewModalData.name}
          isOpen
          onClose={() => setPreviewModalData(null)}
          url={previewModalData.url}
        />
      )}
    </>
  );
};

export default DocumentsTableV2;
